import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Iter 98: React client now targets the versioned prefix `/api/v1`.
// Backend middleware (`middleware_api.ApiVersionRewriteMiddleware`)
// transparently rewrites `/api/v1/*` → `/api/*` so every handler
// keeps working, but responses now stamp `X-Api-Version: v1` — which
// lets us track legacy-caller drift in observability before we sunset
// the unversioned prefix.
export const API = `${BACKEND_URL}/api/v1`;

const ACCESS_KEY = "aura_access_token";
const REFRESH_KEY = "aura_refresh_token";
const LEGACY_KEY = "aura_token";
const ACTIVE_ORG_KEY = "aura_active_org";

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY) || localStorage.getItem(LEGACY_KEY);
}

/** Multi-Organization Phase 2 — the caller's chosen "active organization"
 * (id or slug) is echoed on every request as `X-Aura-Active-Org`. The backend
 * uses it to scope tenant queries when the user is a member of multiple
 * organizations. `null` clears the preference. */
export function getActiveOrg() {
  return localStorage.getItem(ACTIVE_ORG_KEY) || null;
}
export function setActiveOrg(orgIdOrSlug) {
  if (!orgIdOrSlug) localStorage.removeItem(ACTIVE_ORG_KEY);
  else localStorage.setItem(ACTIVE_ORG_KEY, orgIdOrSlug);
  window.dispatchEvent(new CustomEvent("aura:active-org-changed", { detail: orgIdOrSlug }));
}

export function setAuthTokens({ access_token, refresh_token, token }) {
  if (access_token) localStorage.setItem(ACCESS_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  // Keep the legacy field in sync for any code still reading `aura_token`.
  if (token) localStorage.setItem(LEGACY_KEY, token);
  else if (access_token) localStorage.setItem(LEGACY_KEY, access_token);
}

export function clearAuthTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(LEGACY_KEY);
  localStorage.removeItem(ACTIVE_ORG_KEY);
}

export const api = axios.create({ baseURL: API });

/**
 * Normalise any FastAPI / axios error shape to a display-safe string.
 * FastAPI returns different `detail` shapes depending on the error class:
 *   - 400/403/404 (HTTPException(detail=str)):    `detail: "text"`
 *   - 422 (Pydantic ValidationError):             `detail: [{type, loc, msg, ctx, ...}, ...]`
 *   - 428 or custom (HTTPException(detail=dict)): `detail: {error, message, action}`
 * Rendering the raw object/array into a Sonner toast or JSX crashes React
 * with "Objects are not valid as a React child". This helper keeps every
 * error path safe with a single call site pattern:
 *   toast.error(apiErr(e, "Booking failed"))
 */
export function apiErr(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : d?.msg || d?.message || ""))
      .filter(Boolean)
      .join("; ")
      || fallback;
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.msg || detail.error || fallback;
  }
  return err?.message || fallback;
}

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const activeOrg = getActiveOrg();
  if (activeOrg) config.headers["X-Aura-Active-Org"] = activeOrg;
  return config;
});

// -------- Auto refresh on 401 --------
// If a request comes back 401 AND we still have a refresh token, exchange it
// once and retry the original request. Multiple concurrent 401s share the
// same in-flight refresh promise.

let refreshInflight = null;

async function refreshAccessToken() {
  const refresh_token = localStorage.getItem(REFRESH_KEY);
  if (!refresh_token) return null;
  try {
    const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token });
    setAuthTokens(data);
    return data.access_token;
  } catch {
    clearAuthTokens();
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};

    // ---- Normalise FastAPI error `detail` to a display-safe string ----
    // FastAPI returns different shapes:
    //   400/403/404 → `detail: "text"`
    //   422 (Pydantic ValidationError) → `detail: [{type, loc, msg, ...}]`
    //   428 or custom object → `detail: {error, message, action}`
    // Rendering the non-string variants into Sonner or JSX crashes React with
    // "Objects are not valid as a React child". We normalise here once so
    // every `toast.error(err?.response?.data?.detail || "…")` callsite is
    // safe. The original object is preserved as `detail_raw` for the few
    // callers that need it (e.g. the emergency-screen redirect on 428).
    const data = error.response?.data;
    if (data && data.detail !== undefined && typeof data.detail !== "string") {
      const raw = data.detail;
      let msg = "";
      if (Array.isArray(raw)) {
        msg = raw
          .map((d) => (typeof d === "string" ? d : d?.msg || d?.message || ""))
          .filter(Boolean)
          .join("; ");
      } else if (raw && typeof raw === "object") {
        msg = raw.message || raw.msg || raw.error || "";
      }
      data.detail_raw = raw;
      data.detail = msg || "Request failed";
    }

    // ---- Network-error retry (offline) ----
    // When `error.response` is undefined the request never hit the wire (DNS,
    // captive-portal, offline). For SAFE (GET) requests we park the retry in
    // a queue and replay it as soon as the browser fires `online`. Other
    // methods bubble up so the caller can decide what to do — writes are
    // intentionally not retried automatically to avoid duplicated bookings /
    // payments / consents.
    if (!error.response && !original._offlineRetried) {
      const method = (original.method || "get").toLowerCase();
      if (method === "get") {
        original._offlineRetried = true;
        return new Promise((resolve, reject) => {
          const onceOnline = () => {
            api.request(original).then(resolve, reject);
          };
          window.addEventListener("aura:online", onceOnline, { once: true });
          window.addEventListener("online", onceOnline, { once: true });
        });
      }
      return Promise.reject(error);
    }

    if (error.response?.status !== 401) return Promise.reject(error);
    if (original._retriedAfterRefresh) return Promise.reject(error);
    // Do not try to refresh the refresh endpoint itself
    if ((original.url || "").includes("/auth/refresh")) return Promise.reject(error);
    if (!localStorage.getItem(REFRESH_KEY)) return Promise.reject(error);

    refreshInflight = refreshInflight || refreshAccessToken();
    const newAccess = await refreshInflight;
    refreshInflight = null;
    if (!newAccess) return Promise.reject(error);
    original._retriedAfterRefresh = true;
    original.headers = original.headers || {};
    original.headers.Authorization = `Bearer ${newAccess}`;
    return api.request(original);
  },
);
